import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ images, index, onClose, onNavigate }) {
  const open = index !== null && index !== undefined;
  const total = images.length;

  const prev = useCallback(() => onNavigate((index - 1 + total) % total), [index, total, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % total), [index, total, onNavigate]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Galerie"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-imperium-brown/95 backdrop-blur-sm"
          onClick={onClose}
        >
          <button
            type="button"
            data-testid="lightbox-close-button"
            aria-label="Schliessen"
            onClick={onClose}
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-imperium-champagne/40 text-imperium-champagne transition-colors hover:bg-imperium-champagne hover:text-imperium-brown"
          >
            <X className="h-5 w-5" />
          </button>

          <span data-testid="lightbox-counter" className="absolute left-6 top-7 text-[11px] font-label uppercase tracking-[0.3em] text-imperium-champagne/80">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>

          <button
            type="button"
            data-testid="lightbox-prev-button"
            aria-label="Vorheriges Bild"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-imperium-champagne/40 text-imperium-champagne transition-colors hover:bg-imperium-champagne hover:text-imperium-brown sm:left-6"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            data-testid="lightbox-next-button"
            aria-label="Nächstes Bild"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-imperium-champagne/40 text-imperium-champagne transition-colors hover:bg-imperium-champagne hover:text-imperium-brown sm:right-6"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {images[index].type === "video" ? (
            <motion.video
              key={images[index].src}
              src={images[index].src}
              poster={images[index].poster}
              data-testid="lightbox-video"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              controls
              playsInline
              preload="none"
              className="max-h-[82vh] max-w-[88vw]"
            />
          ) : (
            <motion.img
              key={images[index].src}
              src={images[index].src}
              alt={images[index].alt || ""}
              data-testid="lightbox-image"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[82vh] max-w-[88vw] object-contain"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
